import React, { useState, useEffect } from 'react';
import { withStyling, IInjectedStylingProps, IStyles } from './higher-order';
import clsx from 'clsx';
import styles from './checkbox-group.module.scss';
import { Checkbox } from './checkbox';

export type CheckboxItem = {
	id: number | string;
	label: string;
	apiLabel?: string;
};

export interface ICheckboxGroupProps extends IInjectedStylingProps {
	checkboxStyles?: IStyles;
	fullWidth?: boolean;
	groupName: string;
	items: CheckboxItem[];
	maxShown?: number;
	onChange: (items: CheckboxItem[], isChecked: boolean) => void;
	selectedItems: CheckboxItem[];
	disabledItems?: CheckboxItem[];
	showLessLabel?: string;
	showMoreIcon?: React.JSX.Element;
	showMoreLabel?: string;
	singleColumn?: boolean;
	sortByName?: boolean;
	disabled?: boolean;
}

const sortItems = (items: CheckboxItem[]): CheckboxItem[] => {
	const stringValues = items.find((item) => Number.isNaN(Number(item.label)));

	if (stringValues) {
		return items.sort((a, b) => {
			if (a.label < b.label) {
				return -1;
			}
			return +1;
		});
	}
	return items.sort((a, b) => Number(a.label) - Number(b.label));
};

const CheckboxComponent = (props: ICheckboxGroupProps): React.JSX.Element => {
	const [showMore, setShowMore] = useState<boolean>(false);
	const [checkBoxItems, setCheckboxItems] = useState<CheckboxItem[]>([]);
	const maxShown = props.maxShown || undefined;

	useEffect(() => {
		const items = props.sortByName ? sortItems(props.items) : props.items;
		setCheckboxItems(items.slice(0, showMore ? undefined : maxShown));
	}, [props.items, props.maxShown, props.sortByName]);

	useEffect(() => {
		const items = props.sortByName ? sortItems(props.items) : props.items;
		if (showMore) {
			setCheckboxItems(items);
		} else {
			setCheckboxItems(items.slice(0, maxShown));
		}
	}, [showMore]);

	const checkboxClassNames = clsx(
		props.styling?.getStyle(styles, 'checkbox'),
		props.singleColumn && props.styling?.getStyle(styles, 'singleColumn'),
		props.fullWidth && props.styling?.getStyle(styles, 'fullWidth')
	);

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const id = e.currentTarget.name.slice(props.groupName.length);

		const selectedItem = props.items.find((item) => {
			const type = typeof item.id;
			const newId = type === 'number' ? Number(id) : id;
			return item.id === newId;
		});

		if (selectedItem) {
			props.onChange([selectedItem], e.currentTarget.checked);
		}
	};

	return (
		<div className={props.styling?.getStyle(styles, 'checkboxContainer')}>
			<div className={props.styling?.getStyle(styles, 'checkboxWrapper')}>
				{checkBoxItems.length > 0 && (
					<ul className={clsx(props.singleColumn && props.styling?.getStyle(styles, 'singleColumn'))}>
						{checkBoxItems.map((item, key) => (
							<li key={key}>
								<div className={checkboxClassNames}>
									<Checkbox
										name={`${props.groupName + item.id}`}
										onChange={handleCheckboxChange}
										checked={itemIsChecked(item, props.selectedItems)}
										propStyles={props.checkboxStyles || {}}
										fullWidth={props.fullWidth}
										disabled={props.disabled || itemIsDisabled(item, props.disabledItems)}
									>
										{item.label}
									</Checkbox>
								</div>
							</li>
						))}
					</ul>
				)}
			</div>
			{maxShown && props.items.length > maxShown && (
				<button
					className={props.styling?.getStyle(styles, 'showMore')}
					type="button"
					onClick={(): void => setShowMore(!showMore)}
				>
					{props.showMoreIcon && (
						<span
							className={clsx(
								props.styling?.getStyle(styles, 'toggleIcon'),
								showMore && props.styling?.getStyle(styles, 'iconShowMore')
							)}
						>
							{props.showMoreIcon}
						</span>
					)}
					<span className={props.styling?.getStyle(styles, 'showMoreText')}>
						{showMore ? <>{props.showLessLabel || 'Minder tonen'}</> : <>{props.showMoreLabel || 'Meer tonen'}</>}
					</span>
				</button>
			)}
		</div>
	);
};

const itemIsChecked = (item: CheckboxItem, selectedItems?: CheckboxItem[]): boolean => {
	if (!selectedItems) return false;

	return selectedItems.findIndex((selectedItem) => selectedItem.id === item.id) > -1;
};

const itemIsDisabled = (item: CheckboxItem, disabledItems?: CheckboxItem[]): boolean =>
	itemIsChecked(item, disabledItems);

const CheckboxGroup = withStyling(CheckboxComponent);
export { CheckboxGroup };
